import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import './StudentsPage.css';
import {
  Search,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Users,
  SlidersHorizontal,
  ExternalLink,
  Loader2,
  GraduationCap
} from 'lucide-react';

const ITEMS_PER_PAGE = 20;

const StudentsPage = ({ collegeId, onViewProfile }) => {
  const [students, setStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [collegeName, setCollegeName] = useState('');

  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);

  /* ── Fetch ─────────────────────────────────────────── */
  useEffect(() => {
    const fetchStudents = async () => {
      if (!collegeId) { setIsLoading(false); return; }
      try {
        const collegeRes = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/college/profile/${collegeId}`
        );
        const name = collegeRes.data.collegeName;
        setCollegeName(name);

        const studentsRes = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/college/students/${encodeURIComponent(name)}`
        );
        setStudents(studentsRes.data);
      } catch (err) {
        console.error('Failed to fetch students:', err);
        setStudents([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchStudents();
  }, [collegeId]);

  useEffect(() => { setCurrentPage(1); }, [searchQuery, sortField, sortDir]);

  /* ── Sort handler ──────────────────────────────────── */
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  /* ── Derived list ──────────────────────────────────── */
  const processed = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();

    let list = students.filter(s => {
      if (!q) return true;
      return (
        (s.fullName || '').toLowerCase().includes(q) ||
        (s.rollNo || '').toLowerCase().includes(q) ||
        (s.personalEmail || '').toLowerCase().includes(q) ||
        (s.phoneNumber || '').toLowerCase().includes(q)
      );
    });

    if (sortField) {
      list = [...list].sort((a, b) => {
        if (sortField === 'cgpa') {
          const aV = parseFloat(a.cgpa) || 0;
          const bV = parseFloat(b.cgpa) || 0;
          return sortDir === 'asc' ? aV - bV : bV - aV;
        }
        if (sortField === 'rollNo') {
          const aV = (a.rollNo || '').toLowerCase();
          const bV = (b.rollNo || '').toLowerCase();
          return sortDir === 'asc' ? aV.localeCompare(bV) : bV.localeCompare(aV);
        }
        return 0;
      });
    }

    return list;
  }, [students, searchQuery, sortField, sortDir]);

  const totalPages = Math.ceil(processed.length / ITEMS_PER_PAGE);
  const paginated = processed.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const getPageNums = () => {
    const pages = [], max = 5;
    let start = Math.max(1, currentPage - Math.floor(max / 2));
    let end = Math.min(totalPages, start + max - 1);
    if (end - start < max - 1) start = Math.max(1, end - max + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  /* ── Sort icon ─────────────────────────────────────── */
  const SortIcon = ({ field }) => {
    const active = sortField === field;
    return (
      <span className="sp-sort-icons">
        <ChevronUp size={11} className={active && sortDir === 'asc' ? 'si-on' : ''} />
        <ChevronDown size={11} className={active && sortDir === 'desc' ? 'si-on' : ''} />
      </span>
    );
  };

  /* ── Loading ───────────────────────────────────────── */
  if (isLoading) {
    return (
      <div className="sp-loading">
        <div className="sp-loading-card">
          <Loader2 className="sp-spin" size={36} />
          <span>Loading student roster…</span>
        </div>
      </div>
    );
  }

  /* ── Render ────────────────────────────────────────── */
  return (
    <div className="sp-root">

      {/* Background decorative blobs */}
      <div className="sp-bg-blob sp-blob-1" />
      <div className="sp-bg-blob sp-blob-2" />
      <div className="sp-bg-blob sp-blob-3" />

      {/* ── Header ── */}
      <div className="sp-header">
        <div className="sp-header-left">
          <div className="sp-title-icon">
            <GraduationCap size={28} />
          </div>
          <div>
            <h1 className="sp-title">Registered Students</h1>
            {collegeName && <p className="sp-subtitle">{collegeName}</p>}
          </div>
        </div>
        <span className="sp-count-badge">
          <Users size={14} />
          {processed.length} student{processed.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* ── Controls ── */}
      <div className="sp-controls">
        <div className="sp-search-wrap">
          <Search size={16} className="sp-search-icon" />
          <input
            className="sp-search"
            type="text"
            placeholder="Search by name, roll no, email or phone…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="sp-clear-btn" onClick={() => setSearchQuery('')}>✕</button>
          )}
        </div>

        <div className="sp-sort-bar">
          <span className="sp-sort-label">
            <SlidersHorizontal size={13} /> Sort by
          </span>
          <button
            className={`sp-sort-btn ${sortField === 'rollNo' ? 'active roll-active' : ''}`}
            onClick={() => handleSort('rollNo')}
          >
            Roll No <SortIcon field="rollNo" />
          </button>
          <button
            className={`sp-sort-btn ${sortField === 'cgpa' ? 'active cgpa-active' : ''}`}
            onClick={() => handleSort('cgpa')}
          >
            CGPA <SortIcon field="cgpa" />
          </button>
        </div>
      </div>

      {/* Showing info */}
      <p className="sp-showing">
        {searchQuery
          ? `Showing ${paginated.length} of ${processed.length} results for "${searchQuery}"`
          : `Showing ${paginated.length} of ${processed.length} students`}
      </p>

      {/* ── Table card ── */}
      <div className="sp-card">
        <div className="sp-table-wrap">
          <table className="sp-table">
            <thead>
              <tr>
                <th className="sp-th-num">#</th>
                <th>Full Name</th>
                <th className="sp-th-sort" onClick={() => handleSort('rollNo')}>
                  Roll No <SortIcon field="rollNo" />
                </th>
                <th>Email</th>
                <th>Phone</th>
                <th className="sp-th-sort" onClick={() => handleSort('cgpa')}>
                  CGPA <SortIcon field="cgpa" />
                </th>
                <th>Exp.</th>
                <th>Skills</th>
                <th className="sp-th-right">Profile</th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan="9" className="sp-empty">
                    <GraduationCap size={40} className="sp-empty-icon" />
                    <p className="sp-empty-title">
                      {searchQuery ? `No students match "${searchQuery}"` : 'No students found.'}
                    </p>
                    <p className="sp-empty-sub">Try adjusting your search or filters</p>
                  </td>
                </tr>
              ) : (
                paginated.map((student, idx) => (
                  <tr key={student._id} className="sp-row">
                    <td className="sp-td-num">
                      {(currentPage - 1) * ITEMS_PER_PAGE + idx + 1}
                    </td>
                    <td className="sp-td-name">
                      <div className="sp-name-avatar">
                        {(student.fullName || '?')[0].toUpperCase()}
                      </div>
                      {student.fullName}
                    </td>
                    <td className="sp-td-mono">{student.rollNo}</td>
                    <td className="sp-td-secondary">{student.personalEmail}</td>
                    <td className="sp-td-secondary">{student.phoneNumber}</td>
                    <td>
                      {student.cgpa
                        ? <span className="sp-cgpa">{student.cgpa}</span>
                        : <span className="sp-td-dim">—</span>}
                    </td>
                    <td className="sp-td-secondary">
                      {student.totalExperience || '0'} yr{student.totalExperience !== '1' ? 's' : ''}
                    </td>
                    <td className="sp-td-skills">
                      {student.skills
                        ? student.skills.substring(0, 30) + (student.skills.length > 30 ? '…' : '')
                        : <span className="sp-td-dim">N/A</span>}
                    </td>
                    <td className="sp-td-right">
                      <button
                        className="sp-view-btn"
                        onClick={() => onViewProfile(student.userId)}
                      >
                        <ExternalLink size={13} /> View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ── */}
        {totalPages > 1 && (
          <div className="sp-pagination">
            <span className="sp-page-info">
              Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
            </span>
            <div className="sp-page-controls">
              <button
                className="sp-page-btn nav"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={14} />
              </button>
              {getPageNums().map(p => (
                <button
                  key={p}
                  className={`sp-page-btn ${currentPage === p ? 'active' : ''}`}
                  onClick={() => setCurrentPage(p)}
                >
                  {p}
                </button>
              ))}
              <button
                className="sp-page-btn nav"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentsPage;